import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client.js';
import { crcExacto, fecha, fechaHora, cobraIvaAparte, LEYENDA_IVA_INCLUIDO, METODOS_PAGO } from '../utils/format.js';
import Logo from '../components/Logo.jsx';
import { Loading } from '../components/UI.jsx';

// Vista publica del comprobante: la que abre el QR del tiquete.
//
// Se pide al endpoint publico, que no exige sesion. Antes usaba la API
// normal, que si la exige: el cliente escaneaba, recibia un 401 y el
// interceptor lo mandaba al login. De ahi el "el QR lleva al login".
export default function Comprobante() {
  const { token } = useParams();
  const [venta, setVenta] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/publico/comprobante/${token}`)
      .then((r) => setVenta(r.data.data))
      .catch(() => setError(true));
  }, [token]);

  const empresa = venta?.negocio;
  const esCredito = venta?.condicion_venta === '02';
  const nombreMetodo = (m) => METODOS_PAGO.find((x) => x.value === m)?.label || m || '';

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20 }}>
      <div className="card" style={{ padding: 40, textAlign: 'center' }}>
        <h2>Comprobante no disponible</h2>
        <p className="muted" style={{ marginTop: 8 }}>Revisa el enlace que te compartieron.</p>
      </div>
    </div>
  );

  if (!venta) return <Loading />;

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 20 }}>
      <div className="card fade-up" style={{ padding: 28, maxWidth: 380, width: '100%', background: 'var(--surface-solid)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
          {empresa?.logo_url
            ? <img src={empresa.logo_url} alt="" style={{ maxWidth: 180, maxHeight: 80, objectFit: 'contain' }} />
            : <Logo size={34} />}
        </div>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <h3>{empresa?.nombre_comercial}</h3>
          <p className="muted" style={{ fontSize: 13 }}>{venta.numero} · {fechaHora(venta.fecha)}</p>
          {esCredito && (
            <p style={{ fontSize: 13, marginTop: 6 }}>
              <span className="badge badge-warning">Venta a credito</span>
              {venta.fecha_vencimiento && <span className="muted"> · vence {fecha(venta.fecha_vencimiento)}</span>}
            </p>
          )}
        </div>
        <table style={{ width: '100%', fontSize: 14 }}>
          <tbody>
            {(venta.items || []).map((it, idx) => (
              <tr key={`${it.nombre}-${idx}`}>
                <td style={{ padding: '4px 0' }}>{it.cantidad} × {it.nombre}</td>
                <td style={{ textAlign: 'right' }}>{crcExacto(it.total_linea)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '14px 0' }} />
        <div className="flex justify-between" style={{ fontSize: 20, fontWeight: 700 }}>
          <span>Total</span><span style={{ color: 'var(--accent-texto)' }}>{crcExacto(venta.total)}</span>
        </div>
        {esCredito && (
          <div style={{ marginTop: 14, fontSize: 14 }}>
            <div className="muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Abonos</div>
            {(venta.abonos || []).length === 0 ? (
              <p className="muted" style={{ fontSize: 13 }}>Todavia sin abonos</p>
            ) : venta.abonos.map((ab, idx) => (
              <div key={idx} className="flex justify-between" style={{ padding: '3px 0' }}>
                <span className="muted">{fecha(ab.fecha)} · {nombreMetodo(ab.metodo)}</span>
                <span>{crcExacto(ab.monto)}</span>
              </div>
            ))}
            <div className="flex justify-between" style={{ padding: '3px 0' }}>
              <span className="muted">Total abonado</span><span>{crcExacto(venta.monto_pagado)}</span>
            </div>
            <div className="flex justify-between" style={{ marginTop: 6, fontSize: 17, fontWeight: 700 }}>
              <span>Saldo pendiente</span>
              <span style={{ color: Number(venta.saldo) > 0 ? 'var(--danger)' : 'var(--success)' }}>{crcExacto(venta.saldo)}</span>
            </div>
          </div>
        )}
        {!cobraIvaAparte(venta.tipo_comprobante) && (
          <p className="muted" style={{ textAlign: 'center', fontSize: 11, marginTop: 12, fontStyle: 'italic' }}>
            {LEYENDA_IVA_INCLUIDO}
          </p>
        )}
        <p className="muted" style={{ textAlign: 'center', fontSize: 13, marginTop: 16 }}>{empresa?.mensaje_factura}</p>
      </div>
    </div>
  );
}
